import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { AppProvider, useAppContext } from "./context/AppContext";
import Header from "./components/layout/Header";
import LivenessDetection from "./components/liveness/LivenessDetection";
import PassportCapture from "./components/passport/PassportCapture";
import LivenessVideo from "./components/liveness_video/LivenessVideo";
import ConsentForm from "./components/consent/ConsentForm";
import LandingPage from "./components/auth/LandingPage";
import { translations } from "./translations/translations";

const API_BASE = "https://clankless-inflatedly-danae.ngrok-free.dev";
const API_KEY = process.env.REACT_APP_API_KEY;
function ExpiredLinkError({ lang, onRetry }) {
  return (
    <div className="text-center mt-40 p-8">
      <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
        <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-red-500 mb-4">
        {lang === 'fa' ? 'لینک نامعتبر است' : 'Invalid Link'}
      </h2>
      <p className="text-text-gray mb-8">
        {lang === 'fa' 
          ? 'لینک احراز هویت منقضی شده یا نامعتبر می‌باشد. لطفاً دوباره از داشبورد اقدام کنید.' 
          : 'The verification link has expired or is invalid. Please try again from your dashboard.'}
      </p>
      
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <a
          href="https://my.fenefx.com/dashboard/kyc"
          className="inline-flex items-center justify-center gap-2 px-4 py-1 text-xs bg-accent-green text-black font-bold rounded-xl hover:bg-accent-green/90 transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg shadow-accent-green/30"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
          {lang === 'fa' ? 'بازگشت به داشبورد' : 'Back to Dashboard'}
        </a>
        
        <button
          onClick={onRetry}
          className="inline-flex items-center justify-center gap-2 px-4 py-1 border border-text-gray/30 text-text-gray font-bold rounded-xl hover:bg-white/5 transition-all duration-300"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {lang === 'fa' ? 'تلاش مجدد' : 'Try Again'}
        </button>
      </div>
    </div>
  );
}

function AppContent() {
  const { lang } = useAppContext();
  
  const [step, setStep] = useState("");
  const [sessionReady, setSessionReady] = useState(false);
  const [status, setStatus] = useState("Initializing...");
  const [sessionId, setSessionId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [mode, setMode] = useState(null);
  const [linkError, setLinkError] = useState(false);

  const checkExistingSession = useCallback(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (!token) {
      setLinkError(true);
      return;
    }

    console.log("Token from URL:", token);
    
    try {
      const response = await axios.post(
        `${API_BASE}/api/authenticate/decode`,
        { token: token },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': API_KEY
          }
        }
      );
      
      if (!response.data || !response.data.session_id) {
        console.error("Invalid response data");
        setLinkError(true);
        return;
      }
      
      console.log("API Response:", response.data);
      
      const receivedMode = response.data.mode;
      setSessionId(response.data.session_id);
      setStep(response.data.stage);
      setUserId(response.data.user_id);
      setTimeRemaining(response.data.ttl);
      setMode(receivedMode);
      
      localStorage.setItem('session_id', response.data.session_id);
      localStorage.setItem('user_id', response.data.user_id);
      
      console.log("✅ Session data saved to localStorage");
      
    } catch (error) {
      console.error("Error decoding token:", error);
      console.log("Error response:", error.response?.data);
      setLinkError(true);
    }
  }, []);

  useEffect(() => {
    checkExistingSession();
  }, [checkExistingSession]);

  const handleRetry = () => {
    setLinkError(false);
    setStatus("Initializing...");
    checkExistingSession();
  };

  useEffect(() => {
    if (timeRemaining === null || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          localStorage.removeItem('session_id');
          localStorage.removeItem('user_id');
          console.log("⏱️ Session expired! LocalStorage cleared.");
          setLinkError(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  const formatTime = (seconds) => {
    if (seconds === null) return "";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{
      fontFamily: lang === "fa" ? "yekan" : "Arial"
    }} className="min-h-screen max-w-[450px] m-auto">
      <div className="fixed inset-0 opacity-10 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(16,185,129,0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(16,185,129,0.3) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        ></div>
      </div>

      <Header />

      {timeRemaining !== null && timeRemaining > 0 && !linkError && (
        <div className="fixed top-4 left-4 z-50 bg-black/80 backdrop-blur-sm px-4 py-2 rounded-lg border border-accent-green/30">
          <div className="flex items-center gap-2">
            <svg 
              className="w-5 h-5 text-accent-green" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
            <span className={`font-mono text-lg font-bold ${
              timeRemaining < 60 ? 'text-red-500' : 'text-accent-green'
            }`}>
              {formatTime(timeRemaining)}
            </span>
          </div>
        </div>
      )}

      <div className="pt-20 relative z-10">
        {linkError ? (
          <ExpiredLinkError lang={lang} onRetry={handleRetry} />
        ) : step === "landing" ? (
          <LandingPage 
            onAuthenticated={(sessId, usrId) => {
              console.log("Authenticated! Session:", sessId, "User:", usrId);
              setSessionId(sessId);
              setUserId(usrId);
              setSessionReady(true);
              setStep("STEP1");
            }}
          />
        ) : step === "STEP1" ? (
          <LivenessDetection
            sessionIdP={sessionId}
            userIdP={userId}
            mode={mode}
            onComplete={() => {
              console.log("Liveness complete, moving to Passport step");
              setStep("STEP2");
            }}
          />
        ) : step === "STEP2" ? (
          <PassportCapture
            sessionId={sessionId}
            userId={userId}
            mode={mode}
            translations={translations}
            onComplete={() => {
              console.log("Passport complete, moving to Liveness Video");
              setStep("STEP3");
            }}
          />
        ) : step === "STEP3" ? (
          <LivenessVideo
            sessionId={sessionId}
            userId={userId}
            mode={mode}
            translations={translations}
            onComplete={() => {
              if (mode === "automatic") {
                console.log("Liveness video complete, moving to Consent Form");
                setStep("STEP4");
              } else {
                console.log("Liveness video complete - verification finished (semi_automatic)");
                setStep("STEP5");
              }
            }}
          />
        ) : step === "STEP4" ? (
          <ConsentForm
            sessionId={sessionId}
            userId={userId}
            translations={translations}
            onComplete={() => {
              console.log("Consent submitted - verification complete!");
              setStep("STEP5");
            }}
          />
        ) : step === "STEP5" ? (
          <div className="text-center mt-40 p-8">
            <div className="w-20 h-20 bg-accent-green/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-accent-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-accent-green mb-4">
              {lang === 'fa' ? 'تایید هویت کامل شد!' : 'Verification Complete!'}
            </h2>
            <p className="text-text-gray mb-8">
              {lang === 'fa' ? 'فرآیند تایید هویت شما با موفقیت به پایان رسید.' : 'Your identity verification process has been completed successfully.'}
            </p>
            
            <a
              href="https://my.fenefx.com/dashboard/kyc"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-green text-black font-bold rounded-xl hover:bg-accent-green/90 transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg shadow-accent-green/30"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              {lang === 'fa' ? 'بازگشت به داشبورد' : 'Back to Dashboard'}
            </a>
          </div>
        ) : (
          <div className="text-center text-gray-300 mt-40">
            <p className="text-lg animate-pulse">{status}</p>
          </div>
        )}
      </div>

      <div className="fixed top-0 left-0 w-96 h-96 bg-accent-green/5 rounded-full blur-3xl animate-pulse-slow pointer-events-none"></div>
      <div className="fixed bottom-0 right-0 w-96 h-96 bg-accent-golden/5 rounded-full blur-3xl animate-pulse-slow pointer-events-none"></div>
    </div>
  );
}

function App() {
  return (
    <AppProvider>
      <div className="bg-gradient-to-br from-primary-black via-secondary-black to-primary-black">
         <AppContent />
      </div>
    </AppProvider>
  );
}

export default App;